import { useState, type ComponentType, type ReactNode } from "react";
import { NewsCard, type NewsCardOptions, type NewsCardProps } from "./NewsCard";
import type { NewsItem } from "../../data/mock";

type NewsSectionProps = {
  title: string;
  dateLabel?: string;
  items: NewsItem[];
  /** The bookmark of a card clicked: `news.saved` is the state the card shows. */
  onToggleSaved: (news: NewsItem) => void;
  /** Under the cards: a status row, «Carica altre». */
  children?: ReactNode;
  /** The card each item is drawn with: NewsCard, or a wrapper that hands it what it needs. */
  card?: ComponentType<NewsCardProps>;
  /** The options of one card (NewsCardOptions), from its item; none: the card as the lists have it. */
  cardOptions?: (news: NewsItem) => NewsCardOptions;
};

export function NewsSection({
  title,
  dateLabel,
  items,
  onToggleSaved,
  children,
  card: Card = NewsCard,
  cardOptions,
}: NewsSectionProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section className="flex flex-col gap-[15px]">
      <header className="flex items-center justify-between">
        <h2 className="text-xl leading-7 font-semibold text-brand">{title}</h2>
        {dateLabel && <p className="text-right text-xs leading-5 font-medium text-muted">{dateLabel}</p>}
      </header>
      {items.map((news) => (
        <Card
          key={news.id}
          news={news}
          chevron={news.chevron}
          expanded={expanded.has(news.id)}
          onToggleExpanded={() => toggle(news.id)}
          onToggleSaved={() => onToggleSaved(news)}
          {...cardOptions?.(news)}
        />
      ))}
      {children}
    </section>
  );
}

